import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import type { ToolbarItem } from 'devextreme/ui/popup';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { useOpsGridPopupSize } from './useOpsGridPopupSize';
import './opsDataGrid.css';

export interface GridDetailPopupFooterAction {
  text: string;
  icon?: string;
  type?: 'default' | 'danger' | 'normal' | 'success';
  stylingMode?: 'contained' | 'outlined' | 'text';
  onClick: () => void;
  disabled?: boolean;
}

export interface GridDetailPopupProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  /** localStorage key for persisted popup size (use opsGridDetailPopupSizeKey(gridStateStorageKey)). */
  sizeStorageKey: string;
  /** When content exceeds this, the body scrolls (popup still caps at 90vh). */
  maxBodyHeight?: number;
  error?: string | null;
  children: ReactNode;
  /** Left-side footer actions (e.g. Delete, Edit). */
  primaryActions?: GridDetailPopupFooterAction[];
  closeText?: string;
}

export function GridDetailPopup({
  visible,
  title,
  onClose,
  width = 620,
  sizeStorageKey,
  maxBodyHeight = 680,
  error,
  children,
  primaryActions,
  closeText = 'Close'
}: GridDetailPopupProps) {
  const {
    width: popupWidth,
    height: popupHeight,
    hasUserSize,
    isMaximized,
    maximize,
    restoreDefault,
    onResizeEnd
  } = useOpsGridPopupSize(width, sizeStorageKey);
  const popupOptions = useResponsivePopupOptions({
    width: popupWidth,
    height: hasUserSize ? popupHeight : undefined
  });
  const isDesktopPopup = !popupOptions.fullScreen;
  const useFixedSize = isDesktopPopup && hasUserSize && popupHeight != null;
  const toolbarItems = useMemo((): ToolbarItem[] => {
    if (!isDesktopPopup) return [];
    return [
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'revert',
          hint: 'Restore default size',
          stylingMode: 'text',
          onClick: restoreDefault
        }
      },
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'fullscreen',
          hint: isMaximized ? 'Already maximized' : 'Maximize',
          stylingMode: 'text',
          disabled: isMaximized,
          onClick: maximize
        }
      }
    ];
  }, [isDesktopPopup, isMaximized, maximize, restoreDefault]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      title={title}
      showCloseButton
      toolbarItems={toolbarItems}
      dragEnabled={isDesktopPopup}
      resizeEnabled={isDesktopPopup && !isMaximized}
      deferRendering={false}
      shading
      height={popupOptions.fullScreen ? '100%' : useFixedSize ? popupHeight : 'auto'}
      maxHeight={popupOptions.fullScreen ? undefined : '90vh'}
      width={isDesktopPopup ? popupWidth : undefined}
      maxWidth={isDesktopPopup ? '90vw' : undefined}
      fullScreen={popupOptions.fullScreen}
      onResizeEnd={isDesktopPopup ? onResizeEnd : undefined}
      position={{ my: 'center', at: 'center', of: window }}
      wrapperAttr={{
        class: `ops-grid-detail-popup-wrapper${useFixedSize ? ' ops-grid-detail-popup-wrapper--sized' : ''}`
      }}
      contentRender={() => (
        <div
          className={`ops-grid-detail-popup${useFixedSize ? ' ops-grid-detail-popup--sized' : ''}`}
          style={
            popupOptions.fullScreen
              ? undefined
              : ({ '--ops-grid-detail-body-max-height': `${maxBodyHeight}px` } as CSSProperties)
          }
        >
          {error ? (
            <div className="ops-grid-detail-popup__error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="ops-grid-detail-popup__body">
            <div className="ops-grid-detail-popup__content">{children}</div>
          </div>
          <div className="ops-grid-detail-popup__footer">
            <div className="ops-grid-detail-popup__footer-actions">
              {primaryActions?.map((action) => (
                <Button
                  key={action.text}
                  text={action.text}
                  icon={action.icon}
                  type={action.type ?? 'normal'}
                  stylingMode={action.stylingMode ?? 'outlined'}
                  onClick={action.onClick}
                  disabled={action.disabled}
                />
              ))}
            </div>
            <Button text={closeText} onClick={onClose} />
          </div>
        </div>
      )}
    />
  );
}
